/* eslint-disable @typescript-eslint/no-explicit-any */
import { message } from 'ant-design-vue'
import { defineStore } from 'pinia'
import {
  container,
  injectable,
  instanceCachingFactory,
  registry,
  singleton
} from 'tsyringe'
import type { App, Component } from 'vue'
import type { Router } from 'vue-router'
import { CORE_AXIOS, CORE_PLUGIN, CORE_ROUTER, CORE_ROUTER_EXTEND, CORE_USER, Http, RouteExtend, type UserService } from '../../../core'
import type { Plugin } from '../../../plugins'
import { injectOptional } from '../../../tsyringe-extend'
import { PLUGIN_AUTH } from '../BuiltinPluginSymbol'
import { RESETPASSWORD_PATH, SIGNIN_PATH, SIGNUP_PATH } from './constants'
import ResetPassword from './view/ResetPassword.vue'
import ResetPasswordPage from './view/ResetPasswordPage.vue'
import SigninPage from './view/SigninPage.vue'
import SignUpPage from './view/SignUpPage.vue'
import SimpleSigninComponent from './view/SimpleSignin.vue'
import SimpleSigninPage from './view/SimpleSigninPage.vue'

export const LoginPageOptionsInjectKey = 'LoginPageOptionsInjectKey'

export type Slot = {
  type: 'text' | 'component' | 'dynamic' | 'image'
  // string
  text?: string
  // component
  component?: Component
  // dynamic
  componentName?: string
  // image
  src?: string
  alt?: string
}

export type LoginPageOption = {
  /**
   * @description 开启方法
   */
  disabled?: boolean
  /**
   * @description 登录后导向的页面地址
   */
  redirectTo?: string
  /**
   * @description 开启 登录自动退回上一页，退出仍在当前页面
   */
  recordDisabled?: boolean
  /**
   * 记录上一页页面地址
   */
  recordPath?: string
  /**
   * Cookie Key
   */
  cookieKey?: string

  decoratorComponentName?: string

  /**
   * 页面上的题头或者组件
   */
  header?: Slot

  /**
   * 页面上的题头或者组件
   */
  left?: Slot

  /**
   * 页面上的题头或者组件
   */
  right?: Slot

  /**
   * 第三方登录控件
   */
  oauth2Components?: string[]

  /**
   * API 地址
   */
  api?: {
    fetchSmsValidationCode?: string
    validateSmsCode?: string
    signinByCode?: string
  }

  /**
   * 禁止注册
   */
  disableSignUp?: boolean

  signinMode?: 'simple' | 'normal' | 'default'
}

interface LoginPageAction {
  signin(path: string, param: any, msg?: string): void
  signinOnly(path: string, param: any, msg?: string): Promise<boolean>
  signOut(path: string, param: any): void
}

// eslint-disable-next-line @typescript-eslint/ban-types
type LoginPageGetter = {}

export type LoginPageService = LoginPageAction & LoginPageGetter

/**
 * @description 生成Pinia方法
 */
export const useLoginStore = defineStore('loginStore', {
  state: () => {
    return {
      disabled: false,
      recordDisabled: false,
      redirectTo: "/",
      recordPath: "/",
      cookieKey: 'iota',
      api: {
        fetchSmsValidationCode: '/v1/api/validations/smses',
        validateSmsCode: '/v1/api/validations/smses/validate',
        signinByCode: '/v1/api/signin_by_code'
      },
      signinMode: 'normal'
    } as LoginPageOption
  },
  actions: {
    /**
     * @description 登录
     */
    signin(path: string, param: any = {}, msg?: string): void {
      const axios = container.resolve<Http>(CORE_AXIOS)
      const router = container.resolve<Router>(CORE_ROUTER)
      const userService = container.resolve<UserService>(CORE_USER)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      axios.post(path, param).then((res: any) => {
        const saved = userService.setUser({
          user: { ...res, _t: new Date().getTime() }
        })
        if (saved) {
          message.success(msg || '登录成功')
          if (sessionStorage.getItem('toPath') != '' && sessionStorage.getItem('toPath') != undefined) {
            const toPath = sessionStorage.getItem('toPath') as string
            const toQuery = sessionStorage.getItem('toQuery') as string
            router.push({ path: toPath, query: JSON.parse(toQuery) })
          } else {
            router.push({ path: this.$state.redirectTo as string || '/' })
          }
        } else {
          message.error('登录失败，请重新尝试')
        }
      }).catch(() => {
        message.error('登录失败，请重新尝试')
      })
    },
    async signinOnly(path: string, param: any, msg?: string): Promise<boolean> {
      const axios = container.resolve<Http>(CORE_AXIOS)
      const userService = container.resolve<UserService>(CORE_USER)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return new Promise((resolve) => {
        axios.post(path, param).then((res: any) => {
          const saved = userService.setUser({
            user: { ...res, _t: new Date().getTime() }
          })
          if (saved) {
            message.success(msg || '登录成功')
            resolve(true)
          } else {
            message.error('登录失败，请重新尝试')
            resolve(false)
          }
        }).catch(() => {
          message.error('登录失败，请重新尝试')
          resolve(false)
        })
      })
    },
    signOut(path: string, param: any = {}): void {
      const axios = container.resolve<Http>(CORE_AXIOS)
      sessionStorage.removeItem('toPath')
      axios.post(path, param).then(() => {
        const userService = container.resolve<UserService>(CORE_USER)
        userService.reset()
        this.clearCookie()
        // router.push({ path: this.$state.redirectTo || '/' })
        if (this.recordDisabled) {
          window.location.reload()
        } else {
          window.location.href = this.$state.redirectTo as string || '/'
        }
      })
    },
    clearCookie(): void {
      const cookies = document.cookie.split(";")
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i]
        const name = cookie.length > -1 ? cookie.substr(0, cookie.length) : cookie
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT"
      }
    }
  }
})

/**
 * @description 登录服务封装
 */

/**
 * 注册登录服务实例
 */
@singleton()
@registry([
  {
    token: CORE_PLUGIN,
    useFactory: instanceCachingFactory<Plugin<LoginPageOption, LoginPageService>>((c) =>
      c.resolve(DefaultLoginPagePlugin)
    ),
  }
])
@registry([
  {
    token: PLUGIN_AUTH,
    useFactory: instanceCachingFactory<Plugin<LoginPageOption, LoginPageService>>((c) =>
      c.resolve(DefaultLoginPagePlugin)
    ),
  }
])
@injectable()
export class DefaultLoginPagePlugin implements Plugin<LoginPageOption, LoginPageService> {
  id: string | symbol = PLUGIN_AUTH
  options: LoginPageOption
  service: LoginPageService

  constructor(
    @injectOptional(LoginPageOptionsInjectKey)
    options: Partial<LoginPageOption>
  ) {
    const loginStore = useLoginStore()
    this.options = loginStore.$state
    this.service = useLoginStore()
    if (options) {
      loginStore.$patch(options)
    }
  }

  init(app: App<Element>): void {
    app.component('IotaSimpleSignin', SimpleSigninComponent)
    app.component('IotaResetPassword', ResetPassword)
    const { disabled, signinMode } = this.options
    if (!disabled) {
      const router = container.resolve<Router>(CORE_ROUTER)
      const routeExtend = container.resolve<RouteExtend>(CORE_ROUTER_EXTEND)
      // 添加路由地址
      switch (signinMode) {
        case 'simple':
          router.addRoute({ path: SIGNIN_PATH, component: SimpleSigninPage, beforeEnter: [routeExtend.authRouter] })
          break
        case 'normal':
        default:
          router.addRoute({ path: SIGNIN_PATH, component: SigninPage, beforeEnter: [routeExtend.authRouter] })
          break
      }
      if (!this.options.disableSignUp) {
        router.addRoute({ path: SIGNUP_PATH, component: SignUpPage, beforeEnter: [routeExtend.authRouter] })
      }
      router.addRoute({ path: RESETPASSWORD_PATH, component: ResetPasswordPage, beforeEnter: [routeExtend.authRouter] })
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  initialized(): void { }
}
